/** Physical constants and the room-geometry estimates derived from RT60. */

/**
 * Speed of sound in dry air, m/s. 331.3 * sqrt(1 + T/273.15).
 *
 * Worth exposing: 20 C gives 343.2 m/s but a cold garage at 5 C gives 334.3,
 * a 2.7% error - which is 8 cm on a 3 m wall.
 */
export function speedOfSound(temperatureC = 20): number {
  return 331.3 * Math.sqrt(1 + temperatureC / 273.15);
}

/** Round-trip delay in seconds for a reflector at `distanceM`. */
export function distanceToDelay(distanceM: number, temperatureC = 20): number {
  return (2 * distanceM) / speedOfSound(temperatureC);
}

/** Distance to a reflector given the round-trip delay from the direct sound. */
export function delayToDistance(delaySec: number, temperatureC = 20): number {
  return (delaySec * speedOfSound(temperatureC)) / 2;
}

export interface AbsorptionPreset {
  id: string;
  label: string;
  alpha: number;
  hint: string;
}

/**
 * Plausible average absorption coefficients. This is the one genuinely
 * unknowable input in the volume estimate, so it is a user-facing choice
 * rather than a hidden constant.
 */
export const ABSORPTION_PRESETS: AbsorptionPreset[] = [
  { id: 'hard', label: 'Hard', alpha: 0.05, hint: 'Tile, glass, concrete. Bathroom, stairwell, garage.' },
  { id: 'typical', label: 'Typical', alpha: 0.12, hint: 'Painted drywall, a few soft things. Office, kitchen.' },
  { id: 'soft', label: 'Soft', alpha: 0.25, hint: 'Carpet, curtains, sofa, bed. Furnished bedroom.' },
  { id: 'dead', label: 'Dead', alpha: 0.4, hint: 'Heavy treatment. Studio, home cinema.' },
];

export interface RoomEstimate {
  volumeM3: number;
  /** Side length of a cube of the same volume - easier to picture than m3. */
  equivalentCubeSideM: number;
  surfaceAreaM2: number;
  /**
   * Volume across a plausible spread of absorption. Wide, because it is cubic
   * in a quantity nobody can measure by looking - that width is the finding,
   * not a rendering problem.
   */
  volumeLowM3: number;
  volumeHighM3: number;
  alpha: number;
  model: 'sabine' | 'eyring';
}

export interface RoomEstimateOptions {
  /** Average absorption coefficient across all surfaces, 0..1. */
  alpha?: number;
  /**
   * Surface-to-volume shape factor k in S = k * V^(2/3). A cube is 6. Long or
   * flat rooms are higher: a 6x4x2.5 m room is about 6.4.
   */
  shapeFactor?: number;
  /** Eyring handles absorptive rooms better; Sabine is the classic. */
  model?: 'sabine' | 'eyring';
  /** Relative uncertainty applied to alpha for the reported range. */
  alphaUncertainty?: number;
}

/**
 * Room volume from reverberation time.
 *
 * Sabine gives RT60 = 0.161 * V / A with A = S * alpha, which is one equation
 * in two unknowns. Closing it needs an assumption about shape: surface area
 * scales as S = k * V^(2/3), so
 *
 *   V = ( RT60 * k * alpha / 0.161 )^3
 *
 * Be honest about what that costs: volume goes as the *cube* of the assumed
 * absorption. Guess alpha twice too high and the room comes out eight times
 * too big. Calling a tiled bathroom "typical" instead of "hard" turns 10 m3
 * into 145 m3, and both numbers come from the same correct arithmetic.
 *
 * Two things do survive. The linear dimension V^(1/3) is only linear in alpha,
 * so "about 2.2 m across" degrades gracefully where "10 m3" does not. And the
 * shape factor barely matters - a long room and a cubic one of the same volume
 * differ by a few percent, far less than the surface guess.
 *
 * So this is a principled estimate with one dominant assumption, and that
 * assumption belongs in front of the user rather than buried in a constant.
 */
export function estimateRoom(rt60Sec: number, options: RoomEstimateOptions = {}): RoomEstimate {
  const alpha = clampAlpha(options.alpha ?? 0.12);
  const k = options.shapeFactor ?? 6;
  const model = options.model ?? 'sabine';
  const uncertainty = options.alphaUncertainty ?? 0.5;

  const volume = volumeFor(rt60Sec, alpha, k, model);
  const low = volumeFor(rt60Sec, clampAlpha(alpha * (1 - uncertainty)), k, model);
  const high = volumeFor(rt60Sec, clampAlpha(alpha * (1 + uncertainty)), k, model);

  return {
    volumeM3: volume,
    equivalentCubeSideM: Math.cbrt(volume),
    surfaceAreaM2: k * Math.pow(volume, 2 / 3),
    volumeLowM3: Math.min(low, high),
    volumeHighM3: Math.max(low, high),
    alpha,
    model,
  };
}

function volumeFor(rt60Sec: number, alpha: number, k: number, model: 'sabine' | 'eyring'): number {
  if (!(rt60Sec > 0)) return NaN;
  // Eyring replaces alpha with -ln(1 - alpha), which diverges sensibly as a
  // room approaches full absorption instead of predicting a finite RT60.
  const effective = model === 'eyring' ? -Math.log(1 - alpha) : alpha;
  const side = (rt60Sec * k * effective) / 0.161;
  return Math.pow(side, 3);
}

function clampAlpha(alpha: number): number {
  return Math.min(Math.max(alpha, 0.01), 0.95);
}
